/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-funnel-trend-model.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { TextField } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'hooks';
import { PanelBenchmark } from 'types/graphql-global-types';
import { validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import PanelService from 'views/components/central-monitoring/panels/add-panel/service';
import SelectWithCategories from 'views/components/core/select-with-categories';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';

const Step2FunnelTrendModel = forwardRef(
    ({ show, panelState, setPage, page, updateState, updateErrorState, canSave }: any, ref) => {
        const { t } = useTranslation();
        const initialState = {
            dataSource: panelState.dataSource?.dataSource,
            dateField: panelState.dataSource?.dateField,
            flagField: panelState.dataSource?.flagField,
            confidenceIntervalType: panelState.modelParams.confidenceIntervalType,
            modelParamsShow: panelState.modelParams.show,
            upperAmber: panelState.modelParams.upperAmber || 95.5,
            lowerAmber: panelState.modelParams.lowerAmber || 95.5,
            upperRed: panelState.modelParams.upperRed || 98.9,
            lowerRed: panelState.modelParams.lowerRed || 98.9,
            benchmarkType: panelState.modelParams.benchmarkType,
            benchmarkValue: panelState.modelParams.benchmarkValue,
        };
        const initialErrors = {
            dataSource: '',
            dateField: '',
            flagField: '',
            confidenceIntervalType: '',
            modelParamsShow: '',
            upperAmber: '',
            lowerAmber: '',
            upperRed: '',
            lowerRed: '',
            benchmarkType: '',
            benchmarkValue: '',
        } as any;
        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...initialErrors });

        const { modelParamsShow } = PanelConstants;

        const getShowParam = (panel: any): string => {
            if (panel.modelParams.hideUpper) return 'hide-upper';

            if (panel.modelParams.hideLower) return 'hide-lower';

            return 'all';
        };

        useEffect(() => {
            if (panelState)
                setState({
                    dataSource: panelState.dataSource?.dataSource,
                    dateField: panelState.dataSource?.dateField,
                    flagField: panelState.dataSource?.flagField,
                    confidenceIntervalType: panelState.modelParams.confidenceIntervalType,
                    modelParamsShow: getShowParam(panelState),
                    upperAmber: panelState.modelParams.upperAmber,
                    lowerAmber: panelState.modelParams.lowerAmber,
                    upperRed: panelState.modelParams.upperRed,
                    lowerRed: panelState.modelParams.lowerRed,
                    benchmarkType: panelState.modelParams.benchmarkType,
                    benchmarkValue: panelState.modelParams.benchmarkValue,
                });
        }, [panelState]);

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value?: any) => {
            if (fieldName === 'dataSource') {
                setState({ ...state, dataSource: value, dateField: '', flagField: '' });
                updateState({ target: { name: 'dataSource.dataSource', value } });
                updateState({ target: { name: 'dataSource.dateField', value: '' } });
                updateState({ target: { name: 'dataSource.flagField', value: '' } });
            } else {
                setState({ ...state, [fieldName]: value });
                updateState({ target: { name: key || fieldName, value } });
            }
            validate(value, fieldName, length, required);
        };

        const intervalTypeOptions = useMemo(PanelService.getIntervalTypeOptions, []);

        const getModelParamsShowOptions = () =>
            Object.values(modelParamsShow).map((field: string) => ({
                id: field,
                text: t(`admin:${field}`),
                active: true,
                default: false,
            }));

        const modelParamsShowOptions = useMemo(getModelParamsShowOptions, []);

        const getBenchmarkTypeOptions = () => {
            const options = Object.values(PanelBenchmark).map((field: string) => ({
                id: field,
                text: t(`admin:${field}`),
                active: true,
                default: false,
            }));
            options.unshift({ id: '', text: t('admin:selectBenchmarkTypeR'), active: true, default: true });
            return options;
        };

        const benchmarkTypeOptions = useMemo(getBenchmarkTypeOptions, []);

        const checkThresholdValidation = (red: number, amber: number, prefix: string, required?: boolean) => {
            const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);
            const error = {} as any;

            if ((red > 99.9 || red < 80) && required)
                error[`${prefix}Red`] = t('admin:numericValueMinMax', { min: '80.0', max: '99.9' });
            else if (red < amber)
                error[`${prefix}Red`] = t('admin:RedThresholdShouldBeBiggerThenAmberThreshold', {
                    prefix: capitalizeFirstLetter(prefix),
                });
            else error[`${prefix}Red`] = '';

            if ((amber > 99.9 || amber < 80) && required)
                error[`${prefix}Amber`] = t('admin:numericValueMinMax', { min: '80.0', max: '99.9' });
            else if (amber > red)
                error[`${prefix}Amber`] = t('admin:AmberThresholdShouldBeLessThenRedThreshold', {
                    prefix: capitalizeFirstLetter(prefix),
                });
            else error[`${prefix}Amber`] = '';

            return error;
        };

        const checkRequiredFields = (reqFields: string[]) => {
            let isValid = true;
            let newErrors = { ...errors };
            reqFields.forEach((field) => {
                let value = (state as any)[field];
                if (typeof value === 'number') value = `${value}`;

                let error = {};
                if (field === 'benchmarkValue') {
                    if (value >= 1 || value <= 0) {
                        error = { benchmarkValue: t('admin:enterValueBetween', { min: '0', max: '1' }) };
                    }
                } else if (field === 'checkThreshold') {
                    error = {
                        ...checkThresholdValidation(state.upperRed, state.upperAmber, 'upper', true),
                        ...checkThresholdValidation(state.lowerRed, state.lowerAmber, 'lower', true),
                    };
                } else {
                    error = validateFields(value, field, 0, true);
                }
                newErrors = {
                    ...newErrors,
                    ...error,
                };
            });
            if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) isValid = false;

            setErrors({ ...newErrors });
            return isValid;
        };

        const numberValidation = (
            value: number,
            fieldName: string,
            min: number,
            max: number,
            errorText: string,
            equal: boolean,
        ) => {
            let error = { [fieldName]: '' };

            if ((equal && (value >= max || value <= min)) || value > max || value < min)
                error = { [fieldName]: errorText };
            else if (['upperBigRed', 'upperAmber', 'upperRed'].includes(fieldName))
                error = checkThresholdValidation(
                    fieldName === 'upperRed' ? value : state.upperRed,
                    fieldName === 'upperAmber' ? value : state.upperAmber,
                    'upper',
                );
            else if (['lowerBigRed', 'lowerAmber', 'lowerRed'].includes(fieldName))
                error = checkThresholdValidation(
                    fieldName === 'lowerRed' ? value : state.lowerRed,
                    fieldName === 'lowerAmber' ? value : state.lowerAmber,
                    'lower',
                );

            setErrors({
                ...errors,
                ...error,
            });
        };

        const onChangeNumber =
            (fieldName: string, errorText?: string, min?: number, max?: number, key?: string, equal?: boolean) =>
            (event: any) => {
                const { value } = event.target;
                setState({
                    ...state,
                    [fieldName]: +value,
                });
                updateState({ target: { name: key || fieldName, value: +value } });

                if (min !== undefined || max !== undefined)
                    numberValidation(+value, fieldName, min, max, errorText, equal);
            };

        const validationOnUpdate = () => {
            const requiredFields = [
                'dataSource',
                'flagField',
                'dateField',
                'confidenceIntervalType',
                'modelParamsShow',
                'checkThreshold',
                'benchmarkType',
            ];
            if (state.benchmarkType === PanelBenchmark.STUDY_SPECIFIC) requiredFields.push('benchmarkValue');

            return checkRequiredFields(requiredFields);
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                // TODO To pass validation on the back-end. Need to remove after back-end changed
                updateState({ target: { name: 'modelParams.upperBigRed', value: 99.9 } });
                updateState({ target: { name: 'modelParams.lowerBigRed', value: 99.9 } });
                setPage({ ...page, number: 3, available: 3 });
            } else if (page.number !== 2) setPage({ ...page, number: 2, available: page.available });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        };

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr select-with-error">
                        <DataSourceSelector
                            customClassName="add-panel-rowDataSource"
                            fieldSelect="dataSource"
                            onChange={(value: string) => {
                                onChange('dataSource', null, false, 'dataSource.dataSource', value);
                            }}
                            label={t('admin:rowData')}
                            labelField="rowDataSource"
                            customId="add-panel-rowDataSource"
                            showDefault={false}
                            value={state.dataSource}
                            error={errors.dataSource}
                            searchable
                            tag="PANEL"
                            disabled={!canSave}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type select-with-error">
                        <FieldSelector
                            customClassName="add-panel-dateField"
                            fieldSelect="dateField"
                            onChange={(value: string) => {
                                onChange('dateField', null, false, 'dataSource.dateField', value);
                            }}
                            label={t('admin:rowDataDateR')}
                            labelField="dateField"
                            customId="add-panel-dateField"
                            value={state.dateField}
                            error={errors.dateField}
                            dataSource={state.dataSource}
                            formats={[Formats.Date, Formats.PartialDate]}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type mr select-with-error">
                        <FieldSelector
                            customClassName="add-panel-flagField"
                            fieldSelect="flagField"
                            label={t('admin:rowDataFlagR')}
                            labelField="flagField"
                            onChange={(value: string) => {
                                onChange('flagField', null, false, 'dataSource.flagField', value);
                            }}
                            customId="add-panel-flagField"
                            value={state.flagField}
                            error={errors.flagField}
                            dataSource={state.dataSource}
                            formats={[Formats.Bool, Formats.Date, Formats.PartialDate]}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type select-with-error">
                        <SelectWithCategories
                            customClassName="add-panel-confidenceIntervalType"
                            showWarning={false}
                            fieldSelect="confidenceIntervalType"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(
                                    fieldName,
                                    null,
                                    false,
                                    'modelParams.confidenceIntervalType',
                                    event.target?.value,
                                );
                            }}
                            label={t('admin:confidenceIntervalTypeR')}
                            filterable={false}
                            labelField="confidenceIntervalType"
                            dataArray={intervalTypeOptions}
                            customId="add-panel-confidenceIntervalType"
                            currentState={state.confidenceIntervalType}
                            error={errors.confidenceIntervalType}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr select-with-error">
                        <SelectWithCategories
                            customClassName="add-panel-modelParamsShow"
                            showWarning={false}
                            fieldSelect="modelParamsShow"
                            label={t('admin:showHideThresholdsR')}
                            filterable={false}
                            onChange={(fieldName?: string) => (event: any) => {
                                const value = event.target?.value;
                                if (value.match('hide-lower')) {
                                    updateState({ target: { name: 'modelParams.hideLower', value: true } });
                                    updateState({ target: { name: 'modelParams.hideUpper', value: false } });
                                } else if (value.match('hide-upper')) {
                                    updateState({ target: { name: 'modelParams.hideLower', value: false } });
                                    updateState({ target: { name: 'modelParams.hideUpper', value: true } });
                                } else {
                                    updateState({ target: { name: 'modelParams.hideLower', value: false } });
                                    updateState({ target: { name: 'modelParams.hideUpper', value: false } });
                                }
                                onChange(fieldName, null, false, 'modelParams.show', value);
                            }}
                            labelField="modelParamsShow"
                            dataArray={modelParamsShowOptions}
                            customId="add-panel-modelParamsShow"
                            currentState={state.modelParamsShow}
                            error={errors.modelParamsShow}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:upperRedThresholdR')}
                            id="add-panel-upperRed"
                            fullWidth
                            type="number"
                            value={state.upperRed}
                            onChange={onChangeNumber(
                                'upperRed',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.upperRed',
                            )}
                            placeholder={t('admin:enterUpperRedThresholdR')}
                            helperText={
                                <span style={errors.upperRed ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.upperRed
                                        ? errors.upperRed
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.upperRed}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={t('admin:upperAmberThresholdR')}
                            id="add-panel-upperAmber"
                            fullWidth
                            type="number"
                            value={state.upperAmber}
                            onChange={onChangeNumber(
                                'upperAmber',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.upperAmber',
                            )}
                            placeholder={t('admin:enterUpperAmberThresholdR')}
                            helperText={
                                <span style={errors.upperAmber ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.upperAmber
                                        ? errors.upperAmber
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.upperAmber}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:lowerAmberThresholdR')}
                            id="add-panel-lowerAmber"
                            fullWidth
                            type="number"
                            value={state.lowerAmber}
                            onChange={onChangeNumber(
                                'lowerAmber',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.lowerAmber',
                            )}
                            placeholder={t('admin:enterLowerAmberThresholdR')}
                            helperText={
                                <span style={errors.lowerAmber ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.lowerAmber
                                        ? errors.lowerAmber
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.lowerAmber}
                        />
                    </FormControl>
                </div>

                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <TextField
                            label={t('admin:lowerRedThresholdR')}
                            id="add-panel-lowerRed"
                            fullWidth
                            type="number"
                            value={state.lowerRed}
                            onChange={onChangeNumber(
                                'lowerRed',
                                t('admin:numericValueMinMax', { min: '80.0', max: '99.9' }),
                                80,
                                99.9,
                                'modelParams.lowerRed',
                            )}
                            placeholder={t('admin:enterLowerRedThresholdR')}
                            helperText={
                                <span style={errors.lowerRed ? { color: '#ae0015' } : {}} className="remaining-text">
                                    {errors.lowerRed
                                        ? errors.lowerRed
                                        : t('admin:numericValueMinMax', { min: '80.0', max: '99.9' })}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            error={!!errors.lowerRed}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type not-full mr select-with-error">
                        <SelectWithCategories
                            customClassName="add-panel-benchmarkType"
                            showWarning={false}
                            fieldSelect="benchmarkType"
                            label={t('admin:benchmarkTypeR')}
                            filterable={false}
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'modelParams.benchmarkType', event.target?.value);
                            }}
                            labelField="benchmarkType"
                            dataArray={benchmarkTypeOptions}
                            customId="add-panel-benchmarkType"
                            currentState={state.benchmarkType}
                            error={errors.benchmarkType}
                        />
                    </FormControl>
                    {state.benchmarkType === PanelBenchmark.STUDY_SPECIFIC && (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <TextField
                                label={t('admin:benchmarkR')}
                                id="add-panel-benchmarkValue"
                                fullWidth
                                type="number"
                                value={state.benchmarkValue}
                                onChange={onChangeNumber(
                                    'benchmarkValue',
                                    t('admin:enterValueBetween', { min: '0', max: '1' }),
                                    0,
                                    1,
                                    'modelParams.benchmarkValue',
                                    true,
                                )}
                                placeholder={t('admin:enterBenchmarkR')}
                                helperText={
                                    <span
                                        style={errors.benchmarkValue ? { color: '#ae0015' } : {}}
                                        className="remaining-text"
                                    >
                                        {errors.benchmarkValue
                                            ? errors.benchmarkValue
                                            : t('admin:enterValueBetween', { min: '0', max: '1' })}
                                    </span>
                                }
                                variant="outlined"
                                color="secondary"
                                className="before-select"
                            />
                        </FormControl>
                    )}
                </div>
            </div>
        );
    },
);

export default Step2FunnelTrendModel;
